// Derivations related to calculating GDP related data
import { createSelector } from 'reselect'

import usdInflationRate from '../../data/usdInflationRate';
import { IDataPoint, } from '../../services/projections';
import { compoundGrowth } from '../../services/statistics';
import { PROJECTION_START_YEAR, PROJECTION_YEAR_DELTA } from './index';

import {
  CURRENT_YEAR,
  getEconomicGrowthRate,
  getGrossDomesticProduct,
  getLastSeriesValue,
  getNumProjectionYears,
} from '../selectors';
import {
  getProjectedPopulationSeries
} from './population';

// TODO: figure out where the magic numbers (109, 2.1241, 4168, etc) come from.
// baseYear is the year whose dollars you're using to adjust for
const adjustForInflation = (quantity: number, year: number, baseYear: number): number => {
  const baseInflationRate = usdInflationRate[baseYear]; // Inflation in year of interest, historic
  const currentInflationProjection = 2.1241 * year - 4168.7; // Sheet:Macroprojections:G24
  return (quantity * currentInflationProjection) / baseInflationRate;
}

export const getAdjustedGdp = createSelector(
  [getGrossDomesticProduct],
  (gdp): number => {
    // Sheet:Macroprojections:F23 : Inflation from Year whose dollars you're using
    const baseCurrencyYear = 2015;
    return adjustForInflation(gdp, CURRENT_YEAR, baseCurrencyYear);
  }
)

export const getProjectedGrossDomesticProduct = createSelector(
  [getAdjustedGdp, getEconomicGrowthRate, getNumProjectionYears],
  (adjustedGdp, economicGrowthRate, numProjectionYears): number => {
    return compoundGrowth(adjustedGdp, economicGrowthRate, numProjectionYears - PROJECTION_YEAR_DELTA);
  }
)

// Metrics for the line charts
export const getGdpPerCapitaSeries = createSelector(
  [getAdjustedGdp, getEconomicGrowthRate, getProjectedPopulationSeries, getNumProjectionYears],
  (gdp, economicGrowthRate, populationSeries, numProjectionYears): IDataPoint[] => {
    // For some reason we're basing everything on 1996 Dollars
    const baseCurrencyYear = 1996;

    // This formula is a little bit fishy, but it has parity with the spreadsheet
    const gdpPerCapitaSeries = [{
      'x': CURRENT_YEAR,
      'y': adjustForInflation(gdp, CURRENT_YEAR, baseCurrencyYear) / populationSeries[PROJECTION_YEAR_DELTA],
    }];

    let baseGdp = gdp;
    const multiplier = 1 + economicGrowthRate;
    for (let i = 0; i < numProjectionYears; i++) {
      const projectionYear = CURRENT_YEAR + i + 1;
      baseGdp *= multiplier;
      const adjustedGdp = adjustForInflation(baseGdp, projectionYear, baseCurrencyYear);
      const baseGdpPerCapita = adjustedGdp / populationSeries[i + 1 + PROJECTION_YEAR_DELTA];
      gdpPerCapitaSeries.push({
        'x': projectionYear,
        'y': baseGdpPerCapita
      });
    }

    // WARNING: HACKY ADJUSTMENT - we need to cut off the values starting from 2015-2018
    // then we need to prepend some blanks for the earlier years
    const tail = gdpPerCapitaSeries.slice(0, -PROJECTION_YEAR_DELTA);
    const head = [];
    for (let i = 0; i < PROJECTION_YEAR_DELTA; i++) {
      head.push({
        'x': PROJECTION_START_YEAR + i,
        'y': 0,
      })
    }

    const combined = head.concat(tail);
    // console.log("Combined GDP", combined);
    return combined;
  }
)

// Extracts the most recent value... note that it's not scenario specific though.
export const getGdpPerCapita = createSelector(
  [getGdpPerCapitaSeries],
  (gdpSeries): number => {
    return getLastSeriesValue(gdpSeries);
  }
)



// WEBPACK FOOTER //
// ./src/store/selectors/gdp.ts